import React, { Fragment, useState } from 'react';
import { Container, Dropdown, Icon, Menu } from 'semantic-ui-react';
import { withRouter } from 'react-router';
import { Link, NavLink } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import moment from 'moment';
import Svg from 'react-inlinesvg';

import { withAuth, AuthStates, AuthRoles, authProptypes } from '../../auth';
import { Routes } from '../../routing';
import { Validations } from '../../utils';
import { Logo } from '../';
import { withAnalytics } from '../../analytics';
import { Menu as TopMenu } from '../';
import { breakpointsNumber } from '../../styles';
import MobileDropdown from './MobileDropdown';
import { StyledMobileNav, StyledFullscreenNav } from './TopNavigation.style';
import useWindowWidth from '../../hooks/useWindowWidth';
import { largeMonitorBreakpoint } from '../../utils/breakpoints';
import { useMediaQuery } from 'react-responsive';

import { hamburgerIcon } from '../../images';

const CloseIcon = styled(Icon)`
  padding: 0 !important;
`;

export const TopNavigation = props => {
  const [showDesktopNav, setShowDesktopNav] = useState(true);
  const [visible, setVisible] = useState(false);
  const [tabletMenuVisible, setTabletMenuVisible] = useState(false);
  const { windowWidth } = useWindowWidth();

  const {
    authState,
    authActions: { signOut },
    authUser,
    location = {},
    analytics
  } = props;

  const isAuthorized = authUser && authUser.isAuthorized;
  const isRep = Validations.hasRole(authUser, 'roles', [AuthRoles.Sales]);

  const showRep = isAuthorized && !isRep;

  const { givenName, lastName, name } = authUser;
  const username = givenName && lastName ? `${givenName} ${lastName}` : name;
  const isSignedIn = authState === AuthStates.SignedIn;
  const isDesktop = windowWidth > largeMonitorBreakpoint - 1;
  const isTabletOrLarger = useMediaQuery({
    minWidth: breakpointsNumber.tablet
  });
  const isMobile = useMediaQuery({ maxWidth: breakpointsNumber.tablet - 1 });

  const handleHamburgerMenuClicked = () => setVisible(!visible);
  const handleSidebarHide = () => setVisible(false);

  const handleSalesGuidanceClicked = () => {
    handleSidebarHide();
    analytics.trackEvent('Tool Selected', {
      'Link Location': 'Navigation Bar',
      Tool: 'Selling Tool'
    });
  };

  const handleProductComparisonClicked = () => {
    handleSidebarHide();

    analytics.trackEvent('Tool Selected', {
      'Link Location': 'Navigation Bar',
      Tool: 'Comparison Tool'
    });
  };

  const handlePricingHelperClicked = () => {
    handleSidebarHide();

    analytics.trackEvent('Tool Selected', {
      'Link Location': 'Navigation Bar',
      Tool: 'Pricing Helper'
    });
  };

  const logOut = () => {
    analytics.trackEvent('Logout', {
      Timestamp: moment().format('YYYY/MM/DD*HH:mm:ss')
    });
    signOut();
  };

  const links = {
    agi_rep: (
      <Menu.Item
        as={NavLink}
        exact
        to={Routes.rep}
        onClick={handleSidebarHide}
        activeClassName="item--active"
      >
        <span className="item__inner">
          <FormattedMessage
            id="cta.agi_rep"
            description="Nav My AGI Rep page link text"
            defaultMessage="My AGI Rep"
          />
        </span>
      </Menu.Item>
    ),
    pricing_helper: (
      <Menu.Item
        as={NavLink}
        exact
        to={Routes.pricing}
        onClick={handlePricingHelperClicked}
        activeClassName="item--active"
      >
        <span className="item__inner">
          <FormattedMessage
            id="cta.pricing"
            description="Nav Pricing Helper page link text"
            defaultMessage="Pricing Helper"
          />
        </span>
      </Menu.Item>
    ),

    login: (
      <Menu.Item
        as={NavLink}
        to={Routes.login}
        name="login"
        onClick={handleSidebarHide}
      >
        <span className="item__inner">
          <FormattedMessage
            id="cta.login"
            description="Nav login link text"
            defaultMessage="Login"
          />
        </span>
      </Menu.Item>
    ),

    logout: (
      <Menu.Item as={Link} to={Routes.home} name="logout" onClick={logOut}>
        <span className="item__inner">
          <FormattedMessage
            id="cta.logout"
            description="Nav Logout link text"
            defaultMessage="Logout"
          />
        </span>
      </Menu.Item>
    ),

    helpcentre: (
      <Menu.Item
        as={'a'}
        href="https://agidealersupport.zendesk.com/hc/en-us"
        className="link--fade"
        target="agi"
      >
        <div
          style={{
            backgroundColor: '#006a35',
            marginLeft: '25px',
            color: '#ffffff',
            padding: '15px'
          }}
        >
          Additional
          <br />
          Resources
        </div>
      </Menu.Item>
    )
  };

  return (
    <Fragment>
      <div className={visible ? 'menu--visible' : ''}>
        {/* green header */}
        <Menu as="header" fixed="top" secondary>
          <Container fluid>
            <Menu.Item header>
              {isSignedIn && isTabletOrLarger && (
                <Menu.Item
                  data-qa="toggle-menu"
                  onClick={() => {
                    return isDesktop
                      ? setShowDesktopNav(!showDesktopNav)
                      : setTabletMenuVisible(!tabletMenuVisible);
                  }}
                  position="left"
                >
                  <Svg name="sidebar" src={hamburgerIcon} />
                </Menu.Item>
              )}
              <Logo />
            </Menu.Item>

            <Menu.Menu position="right">
              {isSignedIn && isMobile && (
                <Fragment>
                  {links.helpcentre}
                  <Menu.Item
                    data-qa="toggle-menu"
                    onClick={handleHamburgerMenuClicked}
                  >
                    {visible ? (
                      <CloseIcon name="close" />
                    ) : (
                      <Svg name="sidebar" src={hamburgerIcon} />
                    )}
                  </Menu.Item>
                </Fragment>
              )}

              {isTabletOrLarger && (
                <Fragment>
                  {isSignedIn ? (
                    <Fragment>
                      <Dropdown
                        trigger={
                          <Menu.Item>
                            <span
                              className="link--boxed"
                              data-qa="button-top-navigation-logout"
                            >
                              {username}
                            </span>
                            <Icon name="angle down" color="green" />
                          </Menu.Item>
                        }
                        pointing
                        icon={null}
                      >
                        <Dropdown.Menu>
                          {showRep && (
                            <Fragment>
                              {links.agi_rep}
                              <Dropdown.Divider />
                            </Fragment>
                          )}
                          {links.logout}
                        </Dropdown.Menu>
                      </Dropdown>
                      {links.helpcentre}
                    </Fragment>
                  ) : (
                    // hide login link on login page
                    location.pathname !== Routes.login && links.login
                  )}
                </Fragment>
              )}
            </Menu.Menu>
          </Container>
        </Menu>

        {/* top menu only for mobile */}
        {visible && (
          <StyledMobileNav>
            <TopMenu
              mobile
              location={location}
              setMobileMenuVisible={vis => setVisible(vis)}
            />
          </StyledMobileNav>
        )}
        {/* profile dropdown for mobile */}
        {isSignedIn && isMobile && (
          <MobileDropdown
            username={username}
            logOut={logOut}
            showRep={showRep}
          />
        )}
      </div>
      {showDesktopNav && (
        <StyledFullscreenNav tabletMenuVisible={tabletMenuVisible}>
          <TopMenu
            location={location}
            tabletMenuVisible={tabletMenuVisible}
            closeTabletMenu={() => setTabletMenuVisible(false)}
          />
        </StyledFullscreenNav>
      )}
    </Fragment>
  );
};

TopNavigation.propTypes = {
  // from withAuth
  authState: authProptypes.state,
  authActions: authProptypes.actions,
  authUser: authProptypes.user,
  // from withRouter
  location: PropTypes.shape({
    pathname: PropTypes.string
  }),
  analytics: PropTypes.object,
  toggleMenu: PropTypes.func,
  menu: PropTypes.bool
};

export default withAnalytics(withRouter(withAuth(TopNavigation)));
